import { memo, useState, useCallback, type FormEvent } from "react";
import { useI18n } from "@/i18n";
import type { Locale } from "@/i18n";
import { useRouter } from "@/application/stores/routerStore";
import { useSessionStore } from "@/application/stores/sessionStore";
import { signIn, isAuthError } from "@/infrastructure/auth/authService";
import type { AuthErrorCode } from "@/domain/types";
import { colors, spacing, typography, transitions } from "@/design-system/tokens";

const LOCALES: Locale[] = ["es", "en"];

export const LoginPage = memo(function LoginPage() {
  const { t, locale, setLocale } = useI18n();
  const { navigate } = useRouter();
  const setFromToken = useSessionStore(s => s.setFromToken);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscriptionBlocked, setSubscriptionBlocked] = useState(false);

  const togglePassword = useCallback(() => setShowPassword(v => !v), []);

  const getErrorMessage = useCallback(
    (code: AuthErrorCode): string => {
      const map: Record<AuthErrorCode, string> = {
        AUTH_ERROR: t.login.errorInvalid,
        NETWORK_ERROR: t.login.errorNetwork,
        TIMEOUT_ERROR: t.login.errorNetwork,
        UNKNOWN_ERROR: t.login.errorInvalid,
        SUBSCRIPTION_EXPIRED: t.login.errorSubscription,
      };
      return map[code] ?? t.login.errorInvalid;
    },
    [t.login.errorInvalid, t.login.errorNetwork, t.login.errorSubscription]
  );

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setError("");
      setLoading(true);

      const form = e.currentTarget;
      const data = new FormData(form);
      const identifier = (data.get("identifier") as string).trim();
      const password = data.get("password") as string;

      if (!identifier || !password) {
        setError(t.login.errorInvalid);
        setLoading(false);
        return;
      }

      try {
        const token = await signIn(identifier, password);
        setFromToken(token);
        navigate("platform");
      } catch (err) {
        if (isAuthError(err)) {
          if (err.message === "SUBSCRIPTION_EXPIRED") {
            setSubscriptionBlocked(true);
          } else {
            setError(getErrorMessage(err.message as AuthErrorCode));
          }
        } else {
          setError(t.login.errorInvalid);
        }
        setLoading(false);
      }
    },
    [navigate, t.login.errorInvalid, getErrorMessage]
  );

  if (subscriptionBlocked) {
    return <SubscriptionBlockedScreen onBack={() => setSubscriptionBlocked(false)} />;
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f0f0f0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: spacing.xl,
        gap: spacing.xl,
        position: "relative",
      }}
    >
      <LocaleSwitcher locale={locale} onSwitch={setLocale} />

      <div
        style={{
          background: "#fff",
          borderRadius: "24px",
          padding: "48px 40px 40px",
          width: "100%",
          maxWidth: "440px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "28px",
        }}
      >
        <AppIcon />

        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: "8px" }}>
          <h1
            style={{
              fontFamily: typography.fontFamily,
              fontSize: typography.size["2xl"],
              fontWeight: typography.weight.bold,
              color: "#111",
              margin: 0,
              letterSpacing: "-0.02em",
            }}
          >
            {t.login.welcome}
          </h1>
          <p
            style={{
              fontFamily: typography.fontFamily,
              fontSize: typography.size.sm,
              color: "#888",
              margin: 0,
            }}
          >
            {t.login.subtitle}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          noValidate
          style={{ width: "100%", display: "flex", flexDirection: "column", gap: "16px" }}
        >
          <FieldGroup>
            <FieldLabel htmlFor="identifier">{t.login.emailLabel}</FieldLabel>
            <FieldInput
              id="identifier"
              name="identifier"
              type="text"
              placeholder={t.login.emailPlaceholder}
              autoComplete="username"
              required
            />
          </FieldGroup>

          <FieldGroup>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <FieldLabel htmlFor="password">{t.login.passwordLabel}</FieldLabel>
              <button
                type="button"
                onClick={() => navigate("forgot-password")}
                style={{ fontFamily: typography.fontFamily, fontSize: typography.size.xs, color: "#888", background: "transparent", border: "none", cursor: "pointer", textDecoration: "none", transition: `color ${transitions.fast}` }}
                onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.color = "#111")}
                onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.color = "#888")}
              >
                {t.login.forgotPassword}
              </button>
            </div>
            <div style={{ position: "relative" }}>
              <FieldInput
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder={t.login.passwordPlaceholder}
                autoComplete="current-password"
                required
                style={{ paddingRight: "44px" }}
              />
              <button
                type="button"
                onClick={togglePassword}
                aria-label={showPassword ? "Hide password" : "Show password"}
                style={{
                  position: "absolute",
                  right: "14px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#aaa",
                  display: "flex",
                  alignItems: "center",
                  padding: 0,
                  transition: `color ${transitions.fast}`,
                }}
                onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.color = "#555")}
                onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.color = "#aaa")}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </FieldGroup>

          {error && (
            <p
              role="alert"
              style={{
                fontFamily: typography.fontFamily,
                fontSize: typography.size.xs,
                color: colors.error,
                margin: 0,
                textAlign: "center",
              }}
            >
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            aria-busy={loading}
            style={{
              fontFamily: typography.fontFamily,
              fontSize: typography.size.base,
              fontWeight: typography.weight.bold,
              color: "#fff",
              background: loading ? "#555" : "#111",
              border: "none",
              borderRadius: "12px",
              padding: "16px",
              width: "100%",
              cursor: loading ? "not-allowed" : "pointer",
              transition: `background ${transitions.fast}`,
              marginTop: "4px",
              letterSpacing: "-0.01em",
            }}
            onMouseEnter={e => {
              if (!loading) (e.currentTarget as HTMLButtonElement).style.background = "#333";
            }}
            onMouseLeave={e => {
              if (!loading) (e.currentTarget as HTMLButtonElement).style.background = "#111";
            }}
          >
            {loading ? "..." : t.login.submit}
          </button>
        </form>
      </div>

      <button
        onClick={() => navigate("landing")}
        style={{
          fontFamily: typography.fontFamily,
          fontSize: typography.size.sm,
          color: "#888",
          background: "none",
          border: "none",
          cursor: "pointer",
          transition: `color ${transitions.fast}`,
        }}
        onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.color = "#111")}
        onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.color = "#888")}
      >
        {t.login.backHome}
      </button>
    </div>
  );
});

const LocaleSwitcher = memo(function LocaleSwitcher({
  locale,
  onSwitch,
}: {
  locale: Locale;
  onSwitch: (l: Locale) => void;
}) {
  return (
    <div
      role="group"
      aria-label="Language selector"
      style={{
        position: "absolute",
        top: "24px",
        right: "24px",
        display: "flex",
        background: "#fff",
        borderRadius: "9999px",
        padding: "3px",
        gap: "2px",
        border: "1px solid #e8e8e8",
      }}
    >
      {LOCALES.map(l => (
        <button
          key={l}
          onClick={() => onSwitch(l)}
          aria-pressed={locale === l}
          aria-label={`Switch to ${l.toUpperCase()}`}
          style={{
            fontFamily: typography.fontFamily,
            fontSize: "11px",
            fontWeight: typography.weight.bold,
            padding: "5px 12px",
            borderRadius: "9999px",
            border: "none",
            cursor: "pointer",
            transition: `all ${transitions.fast}`,
            background: locale === l ? "#111" : "transparent",
            color: locale === l ? "#fff" : "#888",
            letterSpacing: "0.05em",
          }}
        >
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
});

const AppIcon = memo(function AppIcon() {
  return (
    <div
      style={{
        width: "52px",
        height: "52px",
        background: colors.primary,
        borderRadius: "14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
      aria-hidden="true"
    >
      <svg width="26" height="26" viewBox="0 0 20 20" fill="none">
        <path
          d="M3 7L10 3L17 7V13L10 17L3 13V7Z"
          stroke="white"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <path
          d="M10 3V17M3 7L17 13M17 7L3 13"
          stroke="white"
          strokeWidth="1.1"
          strokeOpacity="0.6"
        />
      </svg>
    </div>
  );
});

function FieldGroup({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {children}
    </div>
  );
}

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      style={{
        fontFamily: typography.fontFamily,
        fontSize: typography.size.sm,
        fontWeight: typography.weight.semibold,
        color: "#333",
      }}
    >
      {children}
    </label>
  );
}

function FieldInput({
  style,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      style={{
        width: "100%",
        fontFamily: typography.fontFamily,
        fontSize: typography.size.sm,
        color: "#111",
        background: "#fff",
        border: "1.5px solid #e5e5e5",
        borderRadius: "10px",
        padding: "13px 16px",
        outline: "none",
        transition: `border-color ${transitions.fast}`,
        boxSizing: "border-box",
        ...style,
      }}
      onFocus={e => (e.currentTarget.style.borderColor = "#111")}
      onBlur={e => (e.currentTarget.style.borderColor = "#e5e5e5")}
      {...props}
    />
  );
}

function EyeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function SubscriptionBlockedScreen({ onBack }: { onBack: () => void }) {
  const { t } = useI18n();
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f0f0f0",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "32px",
        gap: "24px",
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: "24px",
          padding: "48px 40px",
          width: "100%",
          maxWidth: "460px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "24px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            background: "rgba(239,68,68,0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <h1
            style={{
              fontFamily: typography.fontFamily,
              fontSize: typography.size["2xl"],
              fontWeight: typography.weight.bold,
              color: "#111",
              margin: 0,
              letterSpacing: "-0.02em",
            }}
          >
            {t.login.subscriptionTitle}
          </h1>
          <p
            style={{
              fontFamily: typography.fontFamily,
              fontSize: typography.size.sm,
              color: "#666",
              margin: 0,
              lineHeight: "1.5",
            }}
          >
            {t.login.subscriptionMessage}
          </p>
        </div>
        <button
          onClick={onBack}
          style={{
            fontFamily: typography.fontFamily,
            fontSize: typography.size.sm,
            fontWeight: typography.weight.semibold,
            color: "#555",
            background: "transparent",
            border: "1px solid #e0e0e0",
            borderRadius: "10px",
            padding: "12px 24px",
            cursor: "pointer",
            transition: `all ${transitions.fast}`,
          }}
          onMouseEnter={e => {
            const el = e.currentTarget as HTMLButtonElement;
            el.style.borderColor = "#111";
            el.style.color = "#111";
          }}
          onMouseLeave={e => {
            const el = e.currentTarget as HTMLButtonElement;
            el.style.borderColor = "#e0e0e0";
            el.style.color = "#555";
          }}
        >
          {t.login.subscriptionBack}
        </button>
      </div>
    </div>
  );
}
